import fileSize from 'file-size';
import { componentFactoryOf } from 'vue-tsx-support';

import { injectStylesMixin } from '../../mixins/inject-styles';

import styles from './jtn-ui-fileuploader.styles.scss?module';

interface Events {
  onSizeError: boolean;
  onFilesChanged: File[];
  onClickedAddNewFile: void;
}

type File = {
  lastModified: number;
  name: string;
  size: number;
  type: string;
  webkitRelativePath?: string;
  lastModifiedDate?: Date;
};

const CROSS_ICON_SIZE = 10;
const CLIP_ICON_SIZE = 17;

export default componentFactoryOf<Events>()
  .mixin(injectStylesMixin(styles))
  .create({
    name: 'JtnUiFileuploader',
    props: {
      maxSize: {
        type: Number,
        default: 25
      },
      addedFiles: {
        type: Array as () => File[],
        default() {
          return [];
        }
      }
    },
    data() {
      return {
        totalFilesSize: 0,
        files: this.addedFiles
      };
    },
    computed: {
      maxSizeInBytes(): number {
        return this.maxSize * 1024 * 1024;
      },
      maxSizeText(): string {
        return `Не более ${this.maxSize} МБ`;
      },
      errorText(): string {
        return this.totalFilesSize > this.maxSizeInBytes
          ? `Общий размер файлов превышает ${this.maxSize} Мб`
          : '';
      },
      hasFiles(): boolean {
        return this.files?.length > 0;
      }
    },
    watch: {
      files() {
        let totalSize = 0;
        this.files.forEach(file => {
          totalSize += file.size;
        });

        this.totalFilesSize = totalSize;
        this.$emit('filesChanged', this.files);
      },
      errorText() {
        if (this.errorText.length > 0) {
          this.$emit('sizeError', true);
        } else {
          this.$emit('sizeError', false);
        }
      }
    },
    methods: {
      addNewFile() {
        // очистка, чтобы можно было снова загрузить только что удаленный файл
        (this.$refs.fileInput as HTMLInputElement).value = '';

        (this.$refs.fileInput as HTMLInputElement).click();
        this.$emit('clickedAddNewFile');
      },
      onFileChanged() {
        const fileInput = this.$refs.fileInput as HTMLInputElement;
        if (fileInput.files !== null) {
          const uniqueIndexes = this.getUniqueIndexes(
            Array.from(this.files),
            Array.from(fileInput.files)
          );

          if (uniqueIndexes.length > 0) {
            this.files = [
              ...this.files,
              ...uniqueIndexes.map(
                (index: number) => (fileInput.files as FileList)[index]
              )
            ];
          }
        }
      },
      getUniqueIndexes(files: File[], newFiles: File[]) {
        return newFiles
          .map(
            (
              {
                lastModified: newLastModified,
                name: newName,
                size: newSize,
                type: newType
              },
              index
            ) => {
              const checkFilesResults = files.map(
                ({ lastModified, name, size, type }) =>
                  lastModified === newLastModified &&
                  name === newName &&
                  size === newSize &&
                  type === newType
              );

              return checkFilesResults.some(result => result === true) ? -1 : index;
            }
          )
          .filter(index => index !== -1);
      },
      fileSizeText(size: number) {
        return fileSize(size)
          .human('jedec')
          .replace(
            /(KB)|(MB)/,
            m =>
              ({
                B: 'Б',
                KB: 'КБ',
                MB: 'МБ'
              }[m])
          );
      },
      removeFile(fileIndex: number) {
        this.files = this.files.filter((item, index) => fileIndex !== index);
      }
    },
    render() {
      return (
        <div class={styles.fileuploader}>
          <input
            ref="fileInput"
            type="file"
            multiple
            hidden
            onChange={this.onFileChanged}
          />
          <ul class={[styles.filesList, this.hasFiles ? '' : styles.hiddenFileList]}>
            {this.files.map((item: File, index: number) => (
              <li key={index} class={styles.fileItem}>
                <span class={styles.fileName}>{item.name}</span>
                <span class={styles.fileSize}>{this.fileSizeText(item.size)}</span>
                <button
                  class={styles.closeBtn}
                  onClick={() => {
                    this.removeFile(index);
                  }}
                >
                  <svg width={CROSS_ICON_SIZE} height={CROSS_ICON_SIZE}>
                    <use xlinkHref="/dist/legacy/svg-sprites/jtn-critical.42b7545660e4f467e75d4b37a02533e6.svg#jtn-critical-close"></use>
                  </svg>
                </button>
              </li>
            ))}
          </ul>

          <div class={styles.addNew}>
            <svg width={CLIP_ICON_SIZE} height={CLIP_ICON_SIZE}>
              <use xlinkHref="/dist/legacy/svg-sprites/jtn-common.f7875ac1d2b1e964bfa6c21ef5757c8a.svg#jtn-common-attach"></use>
            </svg>
            <button
              class={styles.addLink}
              onClick={(event: Event) => {
                event.preventDefault();
                this.addNewFile();
              }}
            >
              {'прикрепить фото, видео или файл'}
            </button>
            <span class={styles.maxSizeText}>{this.maxSizeText}</span>
          </div>
        </div>
      );
    }
  });
